import { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend,
} from "recharts";
import { supabase } from "../lib/supabase";
import type { CostLogRow } from "../lib/supabase";
import { useSupabaseQuery } from "../hooks/useSupabaseQuery";
import { fmtUsd, fmtInt } from "../lib/format";

const BUDGET_USD = 30; // Phase 2 D#3 monthly cap

export default function Costs() {
  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString();
  }, []);

  const { data, error, loading } = useSupabaseQuery<CostLogRow[]>(
    async () =>
      supabase
        .from("cost_log")
        .select("*")
        .gte("logged_at", since)
        .order("logged_at", { ascending: true })
        .limit(5000) as unknown as Promise<any>,
    [since],
    60_000,
  );

  const { byDay, byLane, byModel, totalUsd, totalCalls, monthUsd } = useMemo(() => {
    const rows = data || [];
    const byDay: Record<string, { date: string; lane1: number; lane2: number; lane3: number }> = {};
    const byLane: Record<number, { calls: number; usd: number; tokens: number }> = {};
    const byModel: Record<string, { calls: number; usd: number; tokens: number }> = {};
    let totalUsd = 0;
    let monthUsd = 0;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    for (const r of rows) {
      totalUsd += r.cost_usd || 0;
      if ((r.logged_at || "") >= monthStart) monthUsd += r.cost_usd || 0;

      const day = (r.logged_at || r.created_at || "").slice(0, 10);
      if (!byDay[day]) byDay[day] = { date: day, lane1: 0, lane2: 0, lane3: 0 };
      const laneKey = r.lane === 1 ? "lane1" : r.lane === 2 ? "lane2" : r.lane === 3 ? "lane3" : null;
      if (laneKey) byDay[day][laneKey] += r.cost_usd || 0;

      const ln = r.lane ?? 0;
      if (!byLane[ln]) byLane[ln] = { calls: 0, usd: 0, tokens: 0 };
      byLane[ln].calls += 1;
      byLane[ln].usd += r.cost_usd || 0;
      byLane[ln].tokens += (r.input_tokens || 0) + (r.output_tokens || 0);

      const mk = r.model || "unknown";
      if (!byModel[mk]) byModel[mk] = { calls: 0, usd: 0, tokens: 0 };
      byModel[mk].calls += 1;
      byModel[mk].usd += r.cost_usd || 0;
      byModel[mk].tokens += (r.input_tokens || 0) + (r.output_tokens || 0);
    }

    return {
      byDay: Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date)),
      byLane,
      byModel,
      totalUsd,
      totalCalls: rows.length,
      monthUsd,
    };
  }, [data]);

  const monthPct = Math.min(100, (monthUsd / BUDGET_USD) * 100);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="MTD spend" value={fmtUsd(monthUsd, 2)} sub={`of ${fmtUsd(BUDGET_USD, 0)} budget`}>
          <div className="mt-2 h-1.5 bg-ink-700 rounded overflow-hidden">
            <div
              className={`h-full ${monthPct > 80 ? "bg-bad" : monthPct > 60 ? "bg-warn" : "bg-ok"}`}
              style={{ width: `${monthPct}%` }}
            />
          </div>
        </Stat>
        <Stat label="30-day spend" value={fmtUsd(totalUsd, 2)} />
        <Stat label="30-day calls" value={fmtInt(totalCalls)} />
      </div>

      <div className="card">
        <h3 className="text-sm font-medium mb-3">Daily cost by lane (30d)</h3>
        {loading && !data && <p className="text-sm text-chrome-400">Loading…</p>}
        {error && <p className="text-sm text-bad">{(error as Error).message}</p>}
        {data && byDay.length === 0 && (
          <p className="text-sm text-chrome-400">No cost data yet.</p>
        )}
        {byDay.length > 0 && (
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={byDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#26262a" />
                <XAxis dataKey="date" stroke="#76767c" fontSize={11} />
                <YAxis stroke="#76767c" fontSize={11} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip
                  contentStyle={{
                    background: "#111113", border: "1px solid #3a3a40", fontSize: 12,
                  }}
                  formatter={(v: number) => fmtUsd(v, 4)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="lane1" stackId="a" fill="#f59e0b" name="Lane 1" />
                <Bar dataKey="lane2" stackId="a" fill="#3b82f6" name="Lane 2" />
                <Bar dataKey="lane3" stackId="a" fill="#8b5cf6" name="Lane 3" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BreakdownTable
          title="By lane"
          rows={Object.entries(byLane).map(([k, v]) => ({
            label: k === "0" ? "—" : `L${k}`, calls: v.calls, usd: v.usd, tokens: v.tokens,
          })).sort((a, b) => b.usd - a.usd)}
        />
        <BreakdownTable
          title="By model"
          rows={Object.entries(byModel).map(([k, v]) => ({
            label: k, calls: v.calls, usd: v.usd, tokens: v.tokens,
          })).sort((a, b) => b.usd - a.usd)}
        />
      </div>
    </div>
  );
}

function Stat({ label, value, sub, children }: {
  label: string; value: string; sub?: string; children?: React.ReactNode;
}) {
  return (
    <div className="card">
      <p className="text-xs text-chrome-400 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-semibold mt-1 font-mono">{value}</p>
      {sub && <p className="text-xs text-chrome-400 mt-0.5">{sub}</p>}
      {children}
    </div>
  );
}

function BreakdownTable({ title, rows }: {
  title: string;
  rows: { label: string; calls: number; usd: number; tokens: number }[];
}) {
  return (
    <div className="card">
      <h3 className="text-sm font-medium mb-3">{title}</h3>
      <table className="w-full text-sm">
        <thead className="text-xs text-chrome-400 uppercase">
          <tr>
            <th className="text-left font-normal pb-2">Key</th>
            <th className="text-right font-normal pb-2">Calls</th>
            <th className="text-right font-normal pb-2">Tokens</th>
            <th className="text-right font-normal pb-2">USD</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.length === 0 && (
            <tr><td colSpan={4} className="text-chrome-400 py-2">—</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.label} className="border-t border-ink-800">
              <td className="py-1.5">{r.label}</td>
              <td className="py-1.5 text-right">{fmtInt(r.calls)}</td>
              <td className="py-1.5 text-right">{fmtInt(r.tokens)}</td>
              <td className="py-1.5 text-right">{fmtUsd(r.usd, 4)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
